import React, { useEffect, useState } from "react";

export type ToastType = "success" | "error" | "warning" | "info";

export interface ToastData {
    id: string;
    message: string;
    type: ToastType;
    duration?: number;
}

interface ToastProps {
    toast: ToastData;
    onRemove: (id: string) => void;
}

const toastStyles: Record<ToastType, { bg: string; border: string; icon: string }> = {
    success: {
        bg: "bg-green-500/20",
        border: "border-green-500/50",
        icon: "✓",
    },
    error: {
        bg: "bg-red-500/20",
        border: "border-red-500/50",
        icon: "✕",
    },
    warning: {
        bg: "bg-amber-500/20",
        border: "border-amber-500/50",
        icon: "⚠",
    },
    info: {
        bg: "bg-cyan-500/20",
        border: "border-cyan-500/50",
        icon: "ℹ",
    },
};

const textColors: Record<ToastType, string> = {
    success: "text-green-400",
    error: "text-red-400",
    warning: "text-amber-400",
    info: "text-cyan-400",
};

const Toast: React.FC<ToastProps> = ({ toast, onRemove }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);
    const duration = toast.duration || 4000;
    const style = toastStyles[toast.type];
    const textColor = textColors[toast.type];

    useEffect(() => {
        // Animation d'entree
        requestAnimationFrame(() => {
            setIsVisible(true);
        });

        // Timer pour commencer l'animation de sortie
        const fadeTimer = setTimeout(() => {
            setIsLeaving(true);
        }, duration - 300);

        // Timer pour supprimer le toast
        const removeTimer = setTimeout(() => {
            onRemove(toast.id);
        }, duration);

        return () => {
            clearTimeout(fadeTimer);
            clearTimeout(removeTimer);
        };
    }, [toast.id, duration, onRemove]);

    return (
        <div
            className={`
                ${style.bg} ${style.border} border rounded-lg p-4 shadow-lg backdrop-blur-sm
                transform transition-all duration-300 ease-out
                ${isVisible && !isLeaving ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"}
                flex items-center gap-3 min-w-[300px] max-w-[400px]
            `}
        >
            <span className={`${textColor} text-lg font-bold`}>{style.icon}</span>
            <p className={`${textColor} text-sm flex-1`}>{toast.message}</p>
            <button
                onClick={() => {
                    setIsLeaving(true);
                    setTimeout(() => onRemove(toast.id), 300);
                }}
                className={`${textColor} hover:opacity-70 transition-opacity text-lg leading-none`}
            >
                ×
            </button>
        </div>
    );
};

interface ToastContainerProps {
    toasts: ToastData[];
    onRemove: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
    return (
        <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
            {toasts.map((toast) => (
                <Toast key={toast.id} toast={toast} onRemove={onRemove} />
            ))}
        </div>
    );
};

export default Toast;
